import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import StreakWidget from "@/components/StreakWidget";
import SmartCalendarWidget from "@/components/SmartCalendarWidget";
import ApiaryMapWidget from "@/components/ApiaryMapWidget";
import { Boxes, CalendarDays, NotebookPen, Camera, ArrowRight, AlertTriangle } from "lucide-react";
import { format, parseISO, isPast } from "date-fns";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [hives, setHives] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/events", { params: { upcoming: true } }),
      api.get("/hives"),
    ]).then(([s, e, h]) => {
      setStats(s.data);
      setEvents(e.data.slice(0, 5));
      setHives(h.data.slice(0, 4));
    });
  }, []);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Welcome back</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">
            Hello, <span className="font-medium">{user?.name?.split(" ")[0]}</span>
          </h1>
        </div>
        <Link to="/app/hives">
          <Button className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="dashboard-add-hive-cta">
            Add a hive <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Hives" value={stats?.hives ?? 0} icon={Boxes} testid="stat-hives" />
        <StatCard label="Inspections" value={stats?.journals ?? 0} icon={NotebookPen} testid="stat-journals" />
        <StatCard label="Upcoming" value={stats?.upcoming_events ?? 0} icon={CalendarDays} testid="stat-upcoming" />
        <StatCard label="Diagnoses" value={stats?.diagnoses ?? 0} icon={Camera} testid="stat-diagnoses" />
      </div>

      <StreakWidget />
      <SmartCalendarWidget />
      <ApiaryMapWidget />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-medium">Upcoming reminders</h2>
            <Link to="/app/events" className="text-sm text-amber-700 hover:underline" data-testid="view-all-events">View all</Link>
          </div>
          {events.length === 0 ? (
            <EmptyState text="No reminders yet. Schedule your next inspection." cta={{ to: "/app/events", label: "Add reminder" }} />
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="upcoming-events-list">
              {events.map((ev) => {
                const past = isPast(parseISO(ev.due_date)) && !ev.completed;
                return (
                  <li key={ev.id} className="py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-stone-900">{ev.title}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {format(parseISO(ev.due_date), "EEE, MMM d · h:mm a")} · {ev.event_type}
                      </div>
                    </div>
                    {past && (
                      <span className="inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-100 rounded-full px-2.5 py-1">
                        <AlertTriangle className="w-3 h-3" /> Overdue
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-medium">Recent hives</h2>
            <Link to="/app/hives" className="text-sm text-amber-700 hover:underline" data-testid="view-all-hives">All</Link>
          </div>
          {hives.length === 0 ? (
            <EmptyState text="You haven't added any hive yet." cta={{ to: "/app/hives", label: "Add hive" }} />
          ) : (
            <ul className="space-y-3" data-testid="recent-hives-list">
              {hives.map((h) => (
                <li key={h.id}>
                  <Link to={`/app/hives/${h.id}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-stone-50 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 grid place-items-center text-amber-900 font-display font-medium">
                      {h.name?.[0]?.toUpperCase() || "H"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{h.name}</div>
                      <div className="text-xs text-stone-500 truncate">{h.location || "—"} · {h.strength}</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</div>
        <Icon className="w-4 h-4 text-stone-400" />
      </div>
      <div className="font-display text-4xl font-light mt-3">{value}</div>
    </div>
  );
}

function EmptyState({ text, cta }) {
  return (
    <div className="py-8 text-center">
      <p className="text-sm text-stone-500">{text}</p>
      {cta && (
        <Link to={cta.to}>
          <Button variant="outline" className="mt-4 rounded-full border-stone-300" data-testid="empty-state-cta">{cta.label}</Button>
        </Link>
      )}
    </div>
  );
}
